import { type NotificationItem } from '@utazzo/shared';
import { UzButton, type UzColumn, UzInput, UzPagination, UzSelect, UzTable } from '@utazzo/ui';
import { ExternalLink } from 'lucide-react';
import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { openNotificationTarget } from '../notification-navigation';
import { useMarkNotificationRead, useMyAllNotifications } from '../use-notifications';

import { PageShell } from '@/components/page-shell';
import { useCurrentUser } from '@/features/auth/use-auth';

type ReadFilter = 'all' | 'unread' | 'read';

const formatDateTime = (iso: string | null | undefined): string => {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * "Inbox" listing for the current user (and direct reports for
 * SALES_LEAD, org-wide for ADMIN/OPS/FINANCE/READ_ONLY).
 *
 * This is NOT the engine log. The server filters to `status = SENT`,
 * so the page surfaces only notifications that were actually
 * delivered to the recipient - the same shape as the bell dropdown,
 * just paginated + searchable. Admins who want the lifecycle log
 * (QUEUED/SKIPPED/FAILED) use the Notifications -> History tab.
 */
export const MyNotificationsPage = (): React.JSX.Element => {
  const me = useCurrentUser();
  const markRead = useMarkNotificationRead();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [eventCode, setEventCode] = useState<string>('');
  const [tagKey, setTagKey] = useState<string>('');
  const [tagValue, setTagValue] = useState<string>('');
  const [readFilter, setReadFilter] = useState<ReadFilter>('all');

  const query = {
    page,
    pageSize,
    sortDir: 'desc' as const,
    sortBy: 'createdAt',
    ...(eventCode ? { eventCode } : {}),
    ...(tagKey && tagValue ? { tagKey: tagKey.trim(), tagValue: tagValue.trim() } : {}),
  };
  const listQuery = useMyAllNotifications(query);
  const all = listQuery.data?.data ?? [];

  // Read/unread filter is applied client-side - the response is
  // small per page and we don't need to round-trip for the toggle.
  const data = all.filter((r) => {
    if (readFilter === 'unread') return r.readAt === null;
    if (readFilter === 'read') return r.readAt !== null;
    return true;
  });

  const scope = scopeLabelForRole(me?.role);
  const showRecipient = isUnscopedRole(me?.role) || me?.role === 'SALES_LEAD';

  const openRow = (row: NotificationItem): void => {
    if (!row.readAt) markRead.mutate(row.id);
    openNotificationTarget(row.tags);
  };

  const columns: ReadonlyArray<UzColumn<NotificationItem>> = [
    {
      id: 'when',
      header: 'When',
      cell: (row) => (
        <span className="font-mono text-[11px] text-zinc-700">{formatDateTime(row.createdAt)}</span>
      ),
      width: '150px',
    },
    {
      id: 'subject',
      header: 'Notification',
      cell: (row) => (
        <div className="flex min-w-0 flex-col gap-0.5">
          <div className="flex items-center gap-1.5">
            {!row.readAt && (
              <span
                className="h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-500"
                aria-label="Unread"
                title="Unread"
              />
            )}
            <span
              className={
                row.readAt
                  ? 'truncate text-sm text-zinc-700'
                  : 'truncate text-sm font-medium text-zinc-900'
              }
            >
              {row.subject ?? row.eventCode}
            </span>
            {row.occurrenceCount > 1 && (
              <span
                className="shrink-0 rounded-full bg-indigo-100 px-1.5 text-[11px] font-semibold text-indigo-700"
                title={`Occurred ${row.occurrenceCount} times`}
              >
                ×{row.occurrenceCount}
              </span>
            )}
          </div>
          {row.bodyText && (
            <p
              className={
                row.readAt
                  ? 'line-clamp-1 text-xs text-zinc-500'
                  : 'line-clamp-1 text-xs text-zinc-600'
              }
            >
              {row.bodyText}
            </p>
          )}
        </div>
      ),
    },
    ...(showRecipient
      ? [
          {
            id: 'recipient',
            header: 'Recipient',
            cell: (row: NotificationItem) => (
              <span className="truncate font-mono text-[11px] text-zinc-700">
                {row.recipientAddress ??
                  (row.recipientUserId ? row.recipientUserId.slice(0, 8) : '-')}
              </span>
            ),
            width: '180px',
          },
        ]
      : []),
    {
      id: 'lead',
      header: 'Lead',
      cell: (row) => {
        const lead = row.lead;
        const leadIdTag = row.tags?.leadId;
        // No lead-related data at all -> nothing to render.
        if (!lead && !leadIdTag) return <span className="text-xs text-zinc-400">-</span>;
        // Tag present but lead enrichment missing (lead deleted /
        // belongs to a row pre-enrichment). Fall back to the chip.
        if (!lead && leadIdTag) {
          return (
            <RouterLink
              to={`/leads/${leadIdTag}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-0.5 rounded bg-indigo-50 px-1.5 py-0.5 font-mono text-[10px] text-indigo-700 ring-1 ring-indigo-200 hover:bg-indigo-100"
            >
              lead {leadIdTag.slice(0, 8)}
              <ExternalLink className="h-2.5 w-2.5" />
            </RouterLink>
          );
        }
        if (!lead) return null;
        return (
          <RouterLink
            to={`/leads/${lead.id}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="flex min-w-0 flex-col gap-0.5 hover:underline"
            title={`Open lead ${lead.reference}`}
          >
            <span className="font-mono text-[10px] text-zinc-500">{lead.reference}</span>
            <span className="truncate text-sm text-zinc-900">{lead.title}</span>
            <div className="flex flex-wrap items-center gap-1">
              {lead.currentStatus && (
                <span
                  className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium"
                  style={{
                    backgroundColor: `${lead.currentStatus.color ?? '#52525b'}22`,
                    color: lead.currentStatus.color ?? '#52525b',
                  }}
                >
                  {lead.currentStatus.name}
                </span>
              )}
              <span className="font-mono text-[10px] text-zinc-500">
                {formatDateTime(lead.createdAt)}
              </span>
            </div>
          </RouterLink>
        );
      },
      width: '280px',
    },
    {
      id: 'customer',
      header: 'Customer',
      cell: (row) => {
        const customer = row.lead?.customer;
        const customerIdTag = row.tags?.customerId;
        if (!customer && !customerIdTag) return <span className="text-xs text-zinc-400">-</span>;
        if (!customer && customerIdTag) {
          return (
            <RouterLink
              to={`/customers/${customerIdTag}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-0.5 rounded bg-emerald-50 px-1.5 py-0.5 font-mono text-[10px] text-emerald-700 ring-1 ring-emerald-200 hover:bg-emerald-100"
            >
              cust {customerIdTag.slice(0, 8)}
              <ExternalLink className="h-2.5 w-2.5" />
            </RouterLink>
          );
        }
        if (!customer) return null;
        return (
          <RouterLink
            to={`/customers/${customer.id}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="truncate text-sm text-zinc-800 hover:text-emerald-700 hover:underline"
            title={`Open customer ${customer.displayName}`}
          >
            {customer.displayName}
          </RouterLink>
        );
      },
      width: '180px',
    },
  ];

  return (
    <PageShell title="My notifications" description={scope}>
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-end gap-2 rounded-md border border-zinc-200 bg-zinc-50 p-2">
          <UzSelect
            label="Show"
            value={readFilter}
            onValueChange={(v: string) => setReadFilter(v as ReadFilter)}
            options={[
              { value: 'all', label: 'All' },
              { value: 'unread', label: 'Unread only' },
              { value: 'read', label: 'Read only' },
            ]}
            containerClassName="w-40"
          />
          <UzInput
            label="Event code"
            placeholder="e.g. lead.assigned"
            value={eventCode}
            onChange={(e) => {
              setPage(1);
              setEventCode(e.target.value);
            }}
            containerClassName="w-56"
          />
          <UzInput
            label="Tag key"
            placeholder="e.g. leadId"
            value={tagKey}
            onChange={(e) => setTagKey(e.target.value)}
            containerClassName="w-36"
          />
          <UzInput
            label="Tag value"
            placeholder="UUID..."
            value={tagValue}
            onChange={(e) => setTagValue(e.target.value)}
            containerClassName="w-56"
          />
          {(tagKey || tagValue) && (
            <UzButton
              variant="ghost"
              size="sm"
              onClick={() => {
                setTagKey('');
                setTagValue('');
                setPage(1);
              }}
            >
              Clear
            </UzButton>
          )}
        </div>
        <UzTable
          columns={columns}
          data={data}
          getRowId={(row) => row.id}
          loading={listQuery.isLoading}
          onRowClick={openRow}
          emptyState={
            <div className="px-3 py-10 text-center text-sm text-zinc-500">
              {readFilter === 'unread'
                ? 'No unread notifications.'
                : 'No notifications match your filters.'}
            </div>
          }
        />
        <UzPagination
          page={page}
          pageSize={pageSize}
          totalItems={listQuery.data?.pagination.totalItems ?? 0}
          onPageChange={setPage}
          pageSizeOptions={[20, 50, 100]}
          onPageSizeChange={(s) => {
            setPage(1);
            setPageSize(s);
          }}
        />
      </div>
    </PageShell>
  );
};

const isUnscopedRole = (role: string | undefined): boolean =>
  role === 'ADMIN' || role === 'OPS' || role === 'FINANCE' || role === 'READ_ONLY';

const scopeLabelForRole = (role: string | undefined): string => {
  if (!role) return '';
  if (isUnscopedRole(role)) return 'Showing the entire organization.';
  if (role === 'SALES_LEAD') return "Showing your notifications + your direct reports'.";
  return 'Showing your notifications only.';
};
